<script lang="ts">
  import { trapFocus } from './focustrap.ts';
  // Quiz-Schloss: N Fragen richtig beantworten (mode 'lock') oder eine einzelne Frage (mode 'single', z. B. im Dialog).
  import type { QuizSession } from '../game/store.ts';
  import { game } from '../game/game.ts';
  import { t } from '../i18n.ts';
  import QuizCard from './QuizCard.svelte';

  let { session }: { session: QuizSession } = $props();
  import { onMount } from 'svelte';
  import { audio } from '../audio/audio.ts';
  // dynamische Musik: Spannung, solange ein Rätsel offen ist
  onMount(() => {
    audio.setTension(true);
    return () => audio.setTension(false);
  });

  let qIndex = $state(0);
  let correct = $state(0);
  let answered = $state(false);
  let lastRight = $state(false);
  let hint = $state(0);
  let solvedByHint = $state(false);
  let jokerUsed = $state(false);
  let jokerLine = $state('');

  function nextHint() {
    if (answered) return;
    hint++;
    if (hint >= 3) {
      solvedByHint = true;
      onAnswered(true);
    }
  }
  function joker() {
    if (jokerUsed || answered) return;
    jokerUsed = true;
    hint = Math.max(hint, 2);
    jokerLine = t('quiz.jokerLine.' + Math.floor(Math.random() * 3));
  }

  const q = $derived(session.questions[qIndex % session.questions.length]);
  const finished = $derived(session.mode === 'single' ? answered : correct >= session.need);

  let nextBtn: HTMLButtonElement | undefined = $state();
  // nach der Antwort: Erklärung und „Weiter“ ins Bild holen (kleine/quer gehaltene Bildschirme)
  $effect(() => {
    if (answered && nextBtn) {
      nextBtn.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      // Fokus auf „Weiter“: die Antwort-Knöpfe sind jetzt gesperrt, der Fokus soll nicht ins Leere fallen
      nextBtn.focus({ preventScroll: true });
    }
  });

  function onAnswered(right: boolean) {
    if (!solvedByHint) game.noteAnswer(right);
    answered = true;
    lastRight = right;
    if (right) correct++;
    if (session.mode === 'lock') game.puzzleProgress(session.puzzle, correct, q.id);
    else game.puzzleProgress('dialog', 0, q.id);
  }

  function next() {
    if (session.mode === 'single') {
      session.resolve(lastRight);
      return;
    }
    if (correct >= session.need) {
      session.resolve(true);
      return;
    }
    qIndex++;
    answered = false;
    hint = 0;
    solvedByHint = false;
    jokerLine = '';
  }

  function onKey(e: KeyboardEvent) {
    if (e.key === 'Escape' && session.mode === 'lock') session.resolve(false);
    else if (answered && e.key === 'Enter') {
      e.preventDefault();
      next();
    }
  }
</script>

<svelte:window onkeydown={onKey} />

<div use:trapFocus class="wrap" role="dialog" aria-modal="true" aria-labelledby="qtitle" aria-describedby="qprompt" data-testid="quiz">
  <div class="lock panel" class:single={session.mode === 'single'}>
    <header>
      <h2 id="qtitle">{session.title}</h2>
      {#if session.mode === 'lock'}
        <button class="btn icon close" onclick={() => session.resolve(false)} aria-label={t('quiz.later')}>✕</button>
      {/if}
    </header>
    {#if session.mode === 'lock'}
      <div class="dials" role="img" aria-label={t('quiz.progress', { n: correct, need: session.need })}>
        {#each Array(session.need) as _, i}
          <span class="dial" class:on={i < correct}></span>
        {/each}
      </div>
    {/if}
    {#key qIndex}
      {#if !answered}
        <div class="helpers">
          <button class="btn small" onclick={nextHint} data-testid="quiz-hint">💡 {t('quiz.hintBtn.' + Math.min(hint, 2))}</button>
          <button class="btn small" onclick={joker} disabled={jokerUsed} data-testid="quiz-joker">🍺 {t('quiz.joker')}</button>
        </div>
        {#if jokerLine}<p class="joker" data-testid="joker-line">{jokerLine}</p>{/if}
      {/if}
      <QuizCard {q} {onAnswered} {hint} {solvedByHint}>
        <button class="btn primary nextbtn" bind:this={nextBtn} onclick={next} data-testid="quiz-next">
          {finished ? (session.mode === 'lock' ? t('quiz.open') : t('quiz.continue')) : t('quiz.next')}
        </button>
      </QuizCard>
    {/key}
  </div>
</div>

<style>
  .wrap {
    position: fixed;
    inset: 0;
    display: grid;
    /* „safe“: ist die Karte höher als der Bildschirm (Querformat), bleibt der Anfang erreichbar */
    place-items: safe center;
    grid-template-columns: minmax(0, 1fr);
    padding: calc(12px + var(--safe-top)) 12px calc(12px + var(--safe-bottom));
    background: rgba(12, 20, 34, 0.55);
    z-index: 35;
    overflow-y: auto;
  }
  /* Handy quer: kompakter, damit Frage und Antworten gemeinsam sichtbar sind */
  @media (max-height: 500px) {
    .lock {
      padding: 10px 14px 12px;
    }
    .lock :global(.dials) {
      transform: scale(0.6);
      margin: -12px 0;
    }
  }
  .lock {
    width: min(560px, 100%);
    min-width: 0;
    padding: 18px 18px 20px;
    background:
      radial-gradient(circle at 50% 0%, rgba(246, 200, 109, 0.14), transparent 60%),
      linear-gradient(180deg, #3d4248, #24272b);
    border-color: var(--brass1);
    animation: in 0.25s ease-out both;
  }
  .lock.single {
    background:
      radial-gradient(circle at 50% 0%, rgba(246, 200, 109, 0.12), transparent 60%),
      linear-gradient(180deg, #24395a, #16243a);
  }
  @keyframes in {
    from {
      opacity: 0;
      transform: scale(0.97);
    }
  }
  header {
    display: flex;
    align-items: center;
    gap: 12px;
  }
  h2 {
    flex: 1;
    margin: 0;
    font-family: var(--font-display);
    color: var(--brass2);
    font-size: 1.3rem;
  }
  .close {
    width: 44px;
    height: 44px;
    min-height: 44px;
    min-width: 44px;
  }
  .dials {
    display: flex;
    justify-content: center;
    gap: 18px;
    margin: 14px 0 6px;
  }
  .dial {
    width: 34px;
    height: 34px;
    border-radius: 50%;
    background: radial-gradient(circle at 35% 30%, #e2c27a, #6f5420);
    box-shadow: inset 0 -3px 6px rgba(0, 0, 0, 0.5), 0 0 0 4px #24272b, 0 0 0 6px #6f5420;
    transition: all 0.4s ease;
  }
  .dial.on {
    background: radial-gradient(circle at 35% 30%, #ffe6ad, #e0a346);
    box-shadow: inset 0 -3px 6px rgba(0, 0, 0, 0.3), 0 0 0 4px #24272b, 0 0 0 6px #e2c27a, 0 0 22px rgba(246, 200, 109, 0.8);
  }
  .helpers {
    display: flex;
    gap: 8px;
    justify-content: center;
    flex-wrap: wrap;
    margin-top: 10px;
  }
  .btn.small {
    min-height: 44px;
    padding: 0.4em 1em;
    font-size: 0.9rem;
  }
  .joker {
    text-align: center;
    font-family: var(--font-hand);
    font-size: 1.25rem;
    color: var(--amber3);
    margin: 6px 0 0;
  }
  .nextbtn {
    justify-self: end;
  }
</style>
